import type { EditRequestDTO, FilePage } from '@/modules/projects/stores/types'
import { accountLabel, accountOf, firstNumberOf } from './accounts'
import { segmentIdOf } from './fromProject'
import { formatPeriod, sameMonthFiled } from './period'
import type {
  Account,
  DatePeriod,
  DocumentPage,
  Statement,
  StatementView,
} from './types'

const ascending = (one: number, other: number) => one - other

export const filesOf = (pages: DocumentPage[]) => [
  ...new Set(pages.map((page) => page.source.fileName)),
]

// A page is named by its sheet in its own upload, so numbers from two uploads cannot be read out
// as one run. Pages that cross uploads are counted instead.
export const pagesReadOut = (pages: DocumentPage[]) => {
  if (pages.length === 0) return 'no pages'
  if (filesOf(pages).length > 1) {
    return `${pages.length} pages from ${filesOf(pages).length} files`
  }
  const sheets = pages.map((page) => page.source.pageInFile).sort(ascending)
  if (sheets.length === 1) return `page ${sheets[0]}`
  const lastSheet = sheets[sheets.length - 1]
  return lastSheet - sheets[0] + 1 === sheets.length
    ? `pages ${sheets[0]} to ${lastSheet}`
    : `${sheets.length} pages`
}

export const sentenceCase = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1)

export const filedUnderLabel = (
  accounts: Account[],
  statement: Statement | null,
) =>
  `${accountLabel(accounts, statement?.accountId ?? null)}, ${formatPeriod(statement?.period ?? null)}`

// The account and the dates stay apart, because a run of picked-up pages usually sits in one
// account across several months and the account only has to be said once.
export interface PickedUpSource {
  statementId: string
  account: string
  period: string
  pages: DocumentPage[]
}

// Where the pages in hand were filed, in file order, one entry per statement they came from.
export const pickedUpFrom = (
  accounts: Account[],
  holderByPage: ReadonlyMap<number, StatementView>,
  pages: DocumentPage[],
): PickedUpSource[] => {
  const sources = new Map<string, PickedUpSource>()
  for (const page of pages) {
    const holder = holderByPage.get(page.number)
    if (!holder) continue
    const found = sources.get(holder.statement.id)
    if (found) {
      found.pages.push(page)
      continue
    }
    sources.set(holder.statement.id, {
      statementId: holder.statement.id,
      account: accountLabel(accounts, holder.statement.accountId),
      period: formatPeriod(holder.statement.period),
      pages: [page],
    })
  }
  return [...sources.values()]
}

const filePageOf = (page: DocumentPage): FilePage => [
  page.source.fileId,
  page.source.pageInFile,
]

interface Handover {
  segmentId: number
  view: StatementView
  pages: DocumentPage[]
}

// The filed statements the picked pages leave, each with its own pages. Null when a page sits in
// no filed statement: it has no rows to hand over, so only a re-cut can read them.
const handoversOf = (
  holderByPage: ReadonlyMap<number, StatementView>,
  picked: DocumentPage[],
  to: StatementView | null,
): Handover[] | null => {
  const handovers = new Map<StatementView, Handover>()
  for (const page of picked) {
    const view = holderByPage.get(page.number)
    const segmentId = view ? segmentIdOf(view.statement.id) : null
    if (!view || segmentId === null) return null
    if (view === to) continue
    const found = handovers.get(view)
    if (found) found.pages.push(page)
    else handovers.set(view, { segmentId, view, pages: [page] })
  }
  return [...handovers.values()]
}

// Filed pages change hands as they are: their rows follow and nothing is read again. Each statement
// sends its own move, last first, since each lands right after `after`. A target with no account
// would take the first move's account, so more than one source goes through the re-cut.
export const relabelMove = (
  holderByPage: ReadonlyMap<number, StatementView>,
  picked: DocumentPage[],
  to: StatementView,
  after: DocumentPage | null,
): EditRequestDTO[] | null => {
  const targetId = segmentIdOf(to.statement.id)
  const handovers = handoversOf(holderByPage, picked, to)
  if (targetId === null || !handovers) return null
  if (to.statement.accountId === null && handovers.length > 1) return null
  return handovers.reverse().map(({ segmentId, pages }) => ({
    kind: 'move_pages',
    segment_id: segmentId,
    target_segment_id: targetId,
    pages: pages.map(filePageOf),
    after: after && filePageOf(after),
  }))
}

// Where a new statement goes: a lane's account, and the month it was dropped on when there is one.
export interface NewStatementPlace {
  accountId: string | null
  period: DatePeriod | null
}

// Every page of one filed statement and nothing else: those pages are already a statement.
const wholeStatementOf = (handovers: Handover[]) => {
  const [only] = handovers
  return handovers.length === 1 && only.pages.length === only.view.pages.length
    ? only
    : null
}

export const isOneWholeStatement = (
  holderByPage: ReadonlyMap<number, StatementView>,
  picked: DocumentPage[],
) => {
  const handovers = handoversOf(holderByPage, picked, null)
  return handovers !== null && wholeStatementOf(handovers) !== null
}

// The dates each row on a page was read with, as `YYYY-MM-DD`.
export type RowDatesOn = (page: DocumentPage) => string[]

const spanOf = (dates: string[]): DatePeriod | null => {
  if (dates.length === 0) return null
  const sorted = [...dates].sort()
  return { start: sorted[0], end: sorted[sorted.length - 1] }
}

// Nothing is read again, so a statement started from loose pages is dated by the rows on them.
// Pages with no rows fall back to the dates of the statements they came from.
const datesOfLoosePages = (
  handovers: Handover[],
  rowDatesOn: RowDatesOn,
): DatePeriod | null => {
  const fromRows = spanOf(
    handovers.flatMap(({ pages }) => pages.flatMap(rowDatesOn)),
  )
  if (fromRows) return fromRows
  const periods = handovers.flatMap(({ view }) =>
    view.statement.period ? [view.statement.period] : [],
  )
  return spanOf(periods.flatMap(({ start, end }) => [start, end]))
}

// Pages dropped on an empty month start a statement there, under the lane's account. A whole
// statement is moved as it is instead, which keeps its printed balances and its verdict.
export const relabelIntoNewStatement = (
  holderByPage: ReadonlyMap<number, StatementView>,
  picked: DocumentPage[],
  place: NewStatementPlace,
  accounts: Account[],
  rowDatesOn: RowDatesOn,
): EditRequestDTO[] | null => {
  const handovers = handoversOf(holderByPage, picked, null)
  const last4 = firstNumberOf(accounts, place.accountId)
  if (!last4 || !handovers || handovers.length === 0) return null
  const whole = wholeStatementOf(handovers)
  if (whole) {
    const filedThere = accountOf(
      accounts,
      whole.view.statement.accountId,
    )?.numbers.includes(last4)
    return wholeStatementInto(whole, place, filedThere ? null : last4)
  }
  const period = place.period ?? datesOfLoosePages(handovers, rowDatesOn)
  return [
    {
      kind: 'start_statement',
      last4,
      start_date: period?.start ?? null,
      end_date: period?.end ?? null,
      sources: handovers.map(({ segmentId, pages }) => ({
        segment_id: segmentId,
        pages: pages.map(filePageOf),
      })),
    },
  ]
}

// refileTo is null when the statement is already filed there: a combined mailing sits in the lane
// of each of its numbers.
const wholeStatementInto = (
  { segmentId, view }: Handover,
  place: NewStatementPlace,
  refileTo: string | null,
): EditRequestDTO[] => {
  const { period } = view.statement
  const newMonth =
    place.period && !(period && sameMonthFiled(period, place.period))
      ? place.period
      : null
  return [
    ...(refileTo
      ? [
          {
            kind: 'refile_statement' as const,
            segment_id: segmentId,
            last4: refileTo,
          },
        ]
      : []),
    ...(newMonth
      ? [
          {
            kind: 'change_statement_period' as const,
            segment_id: segmentId,
            start_date: newMonth.start,
            end_date: newMonth.end,
          },
        ]
      : []),
  ]
}

// Pages taken out of filed statements leave them as they are: the rows on them are removed and
// nothing is read again. Taking every page out of a statement is deleting it, left to the re-cut.
export const takeOutRequests = (
  holderByPage: ReadonlyMap<number, StatementView>,
  picked: DocumentPage[],
): EditRequestDTO[] | null => {
  const handovers = handoversOf(holderByPage, picked, null)
  const emptiesOne = handovers?.some(
    ({ view, pages }) => pages.length >= view.pages.length,
  )
  if (!handovers || emptiesOne) return null
  return handovers.map(({ segmentId, pages }) => ({
    kind: 'take_out_pages',
    segment_id: segmentId,
    pages: pages.map(filePageOf),
  }))
}
